/**
 * ReportContentSheetContent
 *
 * UI component for the report content sheet.
 * Owns the stepper ref and imperative navigation calls.
 * All state and use-case execution lives in useReportContent.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ANIMATION_CONSTANTS } from '@/presentation/constants/animationConstants';
import { ExpauseStepper, type ExpauseStepperRef } from '@/presentation/components/common/stepper/ExpauseStepper';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import { useReportContent } from '../hooks/useReportContent';
import { ReportContentItemsStep } from './steps/ReportContentItemsStep';
import { ReportContentSubmitStep } from './steps/ReportContentSubmitStep';
import { ReportContentTopBar } from './ReportContentTopBar';
import { qaAttr } from '@/presentation/utils/qaAttr';

export interface ReportContentSheetContentProps {
  media: UserMediaEntity;
  onFinish: () => void;
}

export const ReportContentSheetContent: React.FC<ReportContentSheetContentProps> = ({
  media,
  onFinish,
}) => {
  const stepperRef = useRef<ExpauseStepperRef>(null);
  const [currentStep, setCurrentStep] = useState(0);

  const {
    levels,
    submitItem,
    comment,
    isSubmitting,
    isLastStep,
    pendingNavigation,
    clearPendingNavigation,
    setComment,
    handleItemTap,
    handleBack: onBack,
    trimLastLevel,
    handleSubmit,
  } = useReportContent({ media, onFinish });

  useEffect(() => {
    if (pendingNavigation) {
      stepperRef.current?.goNext();
      clearPendingNavigation();
    }
  }, [pendingNavigation]);

  const handleBack = () => {
    if (currentStep === 0) return;
    onBack();
    stepperRef.current?.goPrevious();
    if (!isLastStep) {
      setTimeout(trimLastLevel, ANIMATION_CONSTANTS.duration.normal + 50);
    }
  };

  const steps = useMemo(() => {
    const levelSteps = levels.map((items, index) => ({
      id: `level-${index}`,
      content: (
        <ReportContentItemsStep
          items={items ?? []}
          isLoading={items === null}
          onItemTap={handleItemTap}
        />
      ),
    }));

    const submitStep = {
      id: 'submit',
      content: (
        <ReportContentSubmitStep
          submitItem={submitItem}
          comment={comment}
          isSubmitting={isSubmitting}
          onCommentChange={setComment}
          onSubmit={handleSubmit}
        />
      ),
    };

    return [...levelSteps, submitStep];
  }, [levels, submitItem, comment, isSubmitting, handleItemTap, handleSubmit, setComment]);

  return (
    <div
      {...qaAttr({
        id: 'report-content-sheet',
        status: isSubmitting ? 'submitting' : 'selecting',
      })}
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      <ReportContentTopBar
        showBack={currentStep > 0}
        isLastStep={isLastStep}
        onBack={handleBack}
        onClose={() => isSubmitting ? undefined : onFinish()}
      />
      <div style={{ flex: 1, minHeight: 0 }}>
        <ExpauseStepper
          ref={stepperRef}
          progressProps={{ showProgress: false }}
          initialStep={0}
          animationType="slide"
          onStepChanged={setCurrentStep}
          steps={steps}
        />
      </div>
    </div>
  );
};
